import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import TracePage from './pages/TracePage';
import HlsTest from './pages/Test/HlsTest';
import AdminLogin from './pages/Admin/Login';
import AdminLayout from './pages/Admin/Layout';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminProducts from './pages/Admin/Products';
import AdminProductForm from './pages/Admin/ProductForm';
import AdminBatches from './pages/Admin/Batches';
import AdminBatchForm from './pages/Admin/BatchForm';
import AdminJadeTypes from './pages/Admin/JadeTypes';
import AdminJadeTypeForm from './pages/Admin/JadeTypeForm';
import AdminYs7Devices from './pages/Admin/Ys7Devices';
import AdminYs7DeviceForm from './pages/Admin/Ys7DeviceForm';
import AdminStores from './pages/Admin/Stores';
import AdminStoreForm from './pages/Admin/StoreForm';
import AdminStoreAccounts from './pages/Admin/StoreAccounts';
import AdminStoreAccountForm from './pages/Admin/StoreAccountForm';
import AdminDepartments from './pages/Admin/Departments';
import AdminDepartmentForm from './pages/Admin/DepartmentForm';
import AdminDepartmentAccounts from './pages/Admin/DepartmentAccounts';
import AdminDepartmentAccountForm from './pages/Admin/DepartmentAccountForm';
import AdminPhotoTypePresets from './pages/Admin/PhotoTypePresets';
import AdminPhotoTypes from './pages/Admin/PhotoTypes';
import AdminCategories from './pages/Admin/Categories';
import AdminCategoryForm from './pages/Admin/CategoryForm';
import AdminReturns from './pages/Admin/Returns';
import AdminCustomers from './pages/Admin/Customers';
import AdminAuditLogs from './pages/Admin/AuditLogs';
import AdminWechatPayConfig from './pages/Admin/WechatPayConfig';
import AdminMemberCards from './pages/Admin/MemberCards';
import AdminPointProducts from './pages/Admin/PointProducts';
import AdminConsultants from './pages/Admin/Consultants';
import AdminWxMembers from './pages/Admin/WxMembers';
import AdminPointRedemptions from './pages/Admin/PointRedemptions';
import AdminBanners from './pages/Admin/Banners';
import StoreRedemptions from './pages/Store/Redemptions';
import StoreLogin from './pages/Store/Login';
import StoreLayout from './pages/Store/Layout';
import StoreProducts from './pages/Store/Products';
import DepartmentLogin from './pages/Department/Login';
import DepartmentLayout from './pages/Department/Layout';
import DepartmentBatches from './pages/Department/Batches';
import DepartmentBatchDetail from './pages/Department/BatchDetail';
import DepartmentPresets from './pages/Department/Presets';
import SuperLogin from './pages/Super/Login';
import SuperLayout from './pages/Super/Layout';
import SuperCompanies from './pages/Super/Companies';
import SuperCompanyForm from './pages/Super/CompanyForm';
import SuperCompanyAdmins from './pages/Super/CompanyAdmins';
import SuperInspectionAdmins from './pages/Super/InspectionAdmins';
import InspectionLogin from './pages/Inspection/Login';
import InspectionLayout from './pages/Inspection/Layout';
import InspectionDashboard from './pages/Inspection/Dashboard';
import useAuthStore from './store/authStore';

// 懒加载 ProductImport，将 xlsx 依赖拆分到独立 chunk
const AdminProductImport = React.lazy(() => import('./pages/Admin/ProductImport'));

// 受保护路由（管理员）— 使用公司slug
const ProtectedRoute = ({ children }) => {
  const { companySlug } = useParams();
  const { isAuthenticated } = useAuthStore();
  const token = localStorage.getItem('admin_token');
  return (isAuthenticated || token) ? children : <Navigate to={`/${companySlug}/admin/login`} />;
};

// 门店保护路由
const StoreProtectedRoute = ({ children }) => {
  const { companySlug } = useParams();
  const storeToken = localStorage.getItem('store_token');
  return storeToken ? children : <Navigate to={`/${companySlug}/store/login`} />;
};

// 部门保护路由
const DepartmentProtectedRoute = ({ children }) => {
  const { companySlug } = useParams();
  const deptToken = localStorage.getItem('department_token');
  return deptToken ? children : <Navigate to={`/${companySlug}/department/login`} />;
};

// 超管保护路由
const SuperProtectedRoute = ({ children }) => {
  const superToken = localStorage.getItem('super_token');
  return superToken ? children : <Navigate to="/super/login" />;
};

// 国检保护路由
const InspectionProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('inspection_token');
  return token ? children : <Navigate to="/inspection/login" />;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* 公众溯源查询页面（统一入口，跨公司搜索） */}
        <Route path="/" element={<TracePage />} />
        <Route path="/trace" element={<TracePage />} />

        {/* HLS 测试页面 */}
        <Route path="/test/hls" element={<HlsTest />} />

        {/* ========== 超级管理员 ========== */}
        <Route path="/super/login" element={<SuperLogin />} />
        <Route path="/super" element={
          <SuperProtectedRoute>
            <SuperLayout />
          </SuperProtectedRoute>
        }>
          <Route index element={<SuperCompanies />} />
          <Route path="companies" element={<SuperCompanies />} />
          <Route path="companies/new" element={<SuperCompanyForm />} />
          <Route path="companies/:id/edit" element={<SuperCompanyForm />} />
          <Route path="companies/:id/admins" element={<SuperCompanyAdmins />} />
          <Route path="inspection-admins" element={<SuperInspectionAdmins />} />
        </Route>

        {/* ========== 国检验证 ========== */}
        <Route path="/inspection/login" element={<InspectionLogin />} />
        <Route path="/inspection" element={
          <InspectionProtectedRoute>
            <InspectionLayout />
          </InspectionProtectedRoute>
        }>
          <Route index element={<InspectionDashboard />} />
        </Route>

        {/* ========== 旧路径兼容重定向 ========== */}
        <Route path="/admin/login" element={<Navigate to="/dy/admin/login" />} />
        <Route path="/admin/*" element={<Navigate to="/dy/admin/*" />} />
        <Route path="/store/login" element={<Navigate to="/dy/store/login" />} />
        <Route path="/store/*" element={<Navigate to="/dy/store/*" />} />
        <Route path="/department/login" element={<Navigate to="/dy/department/login" />} />
        <Route path="/department/*" element={<Navigate to="/default/department/*" />} />

        {/* ========== 公司路由（/:companySlug/...） ========== */}

        {/* 管理员登录 */}
        <Route path="/:companySlug/admin/login" element={<AdminLogin />} />

        {/* 管理后台 */}
        <Route path="/:companySlug/admin" element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="products/new" element={<AdminProductForm />} />
          <Route path="products/import" element={<Suspense fallback={null}><AdminProductImport /></Suspense>} />
          <Route path="products/:id/edit" element={<AdminProductForm />} />
          <Route path="batches" element={<AdminBatches />} />
          <Route path="batches/new" element={<AdminBatchForm />} />
          <Route path="batches/:id" element={<AdminBatchForm />} />
          <Route path="batches/:id/edit" element={<AdminBatchForm />} />
          <Route path="jade-types" element={<AdminJadeTypes />} />
          <Route path="jade-types/new" element={<AdminJadeTypeForm />} />
          <Route path="jade-types/:id/edit" element={<AdminJadeTypeForm />} />
          <Route path="ys7-devices" element={<AdminYs7Devices />} />
          <Route path="ys7-devices/new" element={<AdminYs7DeviceForm />} />
          <Route path="ys7-devices/:id/edit" element={<AdminYs7DeviceForm />} />
          <Route path="stores" element={<AdminStores />} />
          <Route path="stores/new" element={<AdminStoreForm />} />
          <Route path="stores/:id/edit" element={<AdminStoreForm />} />
          <Route path="store-accounts" element={<AdminStoreAccounts />} />
          <Route path="store-accounts/new" element={<AdminStoreAccountForm />} />
          <Route path="store-accounts/:id/edit" element={<AdminStoreAccountForm />} />
          <Route path="departments" element={<AdminDepartments />} />
          <Route path="departments/new" element={<AdminDepartmentForm />} />
          <Route path="departments/:id/edit" element={<AdminDepartmentForm />} />
          <Route path="department-accounts" element={<AdminDepartmentAccounts />} />
          <Route path="department-accounts/new" element={<AdminDepartmentAccountForm />} />
          <Route path="department-accounts/:id/edit" element={<AdminDepartmentAccountForm />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="categories/new" element={<AdminCategoryForm />} />
          <Route path="categories/:id/edit" element={<AdminCategoryForm />} />
          <Route path="photo-types" element={<AdminPhotoTypes />} />
          <Route path="photo-type-presets" element={<AdminPhotoTypePresets />} />
          <Route path="returns" element={<AdminReturns />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="audit-logs" element={<AdminAuditLogs />} />
          <Route path="wechat-pay" element={<AdminWechatPayConfig />} />
          <Route path="member-cards" element={<AdminMemberCards />} />
          <Route path="point-products" element={<AdminPointProducts />} />
          <Route path="consultants" element={<AdminConsultants />} />
          <Route path="wx-members" element={<AdminWxMembers />} />
          <Route path="point-redemptions" element={<AdminPointRedemptions />} />
          <Route path="banners" element={<AdminBanners />} />
        </Route>

        {/* 门店登录 */}
        <Route path="/:companySlug/store/login" element={<StoreLogin />} />

        {/* 门店后台 */}
        <Route path="/:companySlug/store" element={
          <StoreProtectedRoute>
            <StoreLayout />
          </StoreProtectedRoute>
        }>
          <Route index element={<StoreProducts />} />
          <Route path="products" element={<StoreProducts />} />
          <Route path="redemptions" element={<StoreRedemptions />} />
        </Route>

        {/* 部门登录 */}
        <Route path="/:companySlug/department/login" element={<DepartmentLogin />} />

        {/* 部门后台 */}
        <Route path="/:companySlug/department" element={
          <DepartmentProtectedRoute>
            <DepartmentLayout />
          </DepartmentProtectedRoute>
        }>
          <Route index element={<DepartmentBatches />} />
          <Route path="batches" element={<DepartmentBatches />} />
          <Route path="batches/:id" element={<DepartmentBatchDetail />} />
          <Route path="presets" element={<DepartmentPresets />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
